import React, {useRef, useState} from 'react';
import {Notification} from '../navigation/page-layout';
import {translateErrorToReactNode} from '../common';
import {RegionId, useAuth} from '../hooks/use-auth';
import {
    useActivateMerchant,
    useGetMultipleMerchants,
    useInactivateMerchant,
    useListMerchantAutomationAudits,
    useListMerchants,
    useOnboardMerchant,
} from '../hooks/use-merchant-automation-api';
import {MerchantAutomationLambda} from '@amzn/f3-intraday-forecast-merchant-automation-api';
import {
    Box,
    Button,
    ColumnLayout,
    Container,
    FormField,
    Header,
    Icon,
    Input,
    Modal,
    Multiselect,
    Pagination,
    ProgressBar,
    SelectProps,
    SpaceBetween,
    Table,
    TextContent,
} from '@amzn/awsui-components-react';
import {MerchantAutomationAuditList} from '@amzn/f3-intraday-forecast-merchant-automation-api/clients/merchantautomationlambda';

function buildFilterMapAsJsonString(siteId: string, selectedAlertOptions: SelectProps.Options) {
    const filterEquals = {};
    if (siteId) {
        filterEquals['siteId'] = siteId;
    }
    const onboardingAlerts = selectedAlertOptions.map((option: SelectProps.Option) => option.value!);
    if (onboardingAlerts.includes('has_onboarding_errors')) {
        filterEquals['hasOnboardingErrors'] = true;
    }
    if (onboardingAlerts.includes('has_onboarding_warnings')) {
        filterEquals['hasOnboardingWarnings'] = true;
    }
    /**
     * Change value to 'almPhysicalStoreId' once IUDF is WRAP compliant
     */
    filterEquals['nodeIdType'] = 'almPhysicalStoreId';
    return JSON.stringify(filterEquals);
}

export function MerchantAutomation(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();

    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [selectedItems, setSelectedItems] = useState([] as MerchantAutomationLambda.Types.Merchant[]);
    const [visible, setVisible] = useState(false);
    const [alertMessages, setAlertMessages] = useState<string[]>([]);
    const [nextToken, setNextToken] = useState<string>();
    const [pageSize, setPageSize] = useState(50);
    const [nodeIdS, setNodeIdS] = useState<string[]>([]);
    const [nodeIdSearch, setNodeIdSearch] = useState('');
    const [siteId, setSiteId] = useState<string>('');
    const [paginationDisabled, setPaginationDisabled] = useState(false);
    const [localMerchants, setLocalMerchants] = useState(new Map<number, MerchantAutomationLambda.Types.Merchant[]>());
    const [selectedOptions, setSelectedOptions] = useState<SelectProps.Options>([]);
    const [selectedAlertOptions, setSelectedAlertOptions] = useState<SelectProps.Options>([]);

    const isLoading = useRef<boolean>(true);
    const pageCount = useRef(1);
    const storeCount = useRef(0);
    const initialized = useRef(false);

    const endpoints = auth.authInformation!.getEndpoints();
    const canManageMerchants =
        (auth.authInformation!.globalScopes && auth.authInformation!.globalScopes['merchants']?.split(',').includes('manage')) ??
        false;
    /**
     * Use any region to get the "global" client configuration from the list of endpoints, we will use the default "NA" region for now
     */
    const clientConfiguration = endpoints[RegionId.NA].MerchantAutomation;

    function createErrorListener<T>(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {
        execute: excuteListMerchant,
        status: listMerchantsStatus,
        value: listMerchantsResponse,
    } = useListMerchants(clientConfiguration, createErrorListener('ListMerchant failed'), [auth, nextToken]);

    isLoading.current = listMerchantsStatus === 'idle' || listMerchantsStatus === 'pending';
    if (!initialized.current) {
        /**
         * Change value of nodeIdType to 'almPhysicalStoreId' once IUDF is WRAP compliant
         */
        excuteListMerchant({maxResults: pageSize, filterEquals: JSON.stringify({nodeIdType: 'almPhysicalStoreId'})});
        initialized.current = true;
    }

    const {getMultipleMerchantFetchStatus, getMultipleMerchants} = useGetMultipleMerchants(clientConfiguration, [auth]);
    const {execute: executeOnboardMerchant} = useOnboardMerchant(
        clientConfiguration,
        createErrorListener('OnboardMerchant failed'),
        [auth]
    );
    const {execute: executeActivateMerchant} = useActivateMerchant(
        clientConfiguration,
        createErrorListener('ActivateMerchant failed'),
        [auth]
    );
    const {execute: executeInactivateMerchant} = useInactivateMerchant(
        clientConfiguration,
        createErrorListener('InactivateMerchant failed'),
        [auth]
    );

    const {execute: executeListMerchantAudits} = useListMerchantAutomationAudits(
        clientConfiguration,
        createErrorListener('ListMerchantAutomationAudits failed'),
        [auth]
    );

    /**
     * Only load initial set of stores if localMerchants is empty
     */
    if (!isLoading.current && localMerchants.size === 0 && !paginationDisabled) {
        setLocalMerchants(localMerchants.set(currentPageIndex, listMerchantsResponse?.merchants ?? []));
        storeCount.current = storeCount.current + (localMerchants.get(currentPageIndex)?.length ?? 0);
        setNextToken(listMerchantsResponse?.nextToken);
    }

    const columnDefinitions = [
        {
            id: 'almPhysicalStoreId',
            header: 'ALM Physical Store Id',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => item.nodeId,
            minWidth: 50,
        },
        {
            id: 'siteId',
            header: 'Warehouse Id',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => item.siteId,
            minWidth: 50,
        },
        {
            id: 'business',
            header: 'Business',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => item.business,
            minWidth: 50,
        },
        {
            id: 'country',
            header: 'Country',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => item.country,
            minWidth: 50,
        },
        {
            id: 'logisticTypes',
            header: 'Logistic Types',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => item.logisticTypes?.join(', '),
            minWidth: 50,
        },
        {
            id: 'launchDate',
            header: 'Launch Date',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => (
                <TextContent>
                    <b>{item.liveDate ?? 'Launch date not defined'}</b>
                    <br></br>Store timezone: {item.merchantTimezone}
                </TextContent>
            ),
            minWidth: 300,
        },
        {
            id: 'onboardingStatus',
            header: 'IUDF Onboarding Status',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => (
                <ProgressBar
                    value={item.onboardingCompletionPercentage ?? 0}
                    description={item.onboardingStatusDescription}
                    label={
                        (item.onboardingCompletionPercentage ?? 0) === 0
                            ? 'Onboarding not started'
                            : (item.onboardingCompletionPercentage ?? 0) === 100
                            ? 'Onboarding completed'
                            : 'Onboarding in progress'
                    }
                />
            ),
            minWidth: 50,
        },
        {
            id: 'iudfAudits',
            header: 'IUDF Audits',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => {
                return (
                    <Button
                        variant="link"
                        data-testid="open-merchant-info-modal"
                        onClick={async () => {
                            const auditData: MerchantAutomationAuditList = (
                                await executeListMerchantAudits({
                                    eventType: 'ONBOARDING',
                                    nodeId: item.nodeId,
                                })
                            ).merchantAutomationAuditList;
                            const alertMessages = auditData.map((alert) => JSON.stringify(alert, undefined, 4));
                            setAlertMessages(alertMessages);
                            setVisible(true);
                        }}
                    >
                        <Icon name="status-info" />
                    </Button>
                );
            },
            minWidth: 50,
        },
        {
            id: 'alerts',
            header: 'Alerts',
            cell: (item: MerchantAutomationLambda.Types.Merchant) => {
                if (item.hasOnboardingErrors || item.hasOnboardingWarnings) {
                    return (
                        <Button
                            variant="link"
                            data-testid="open-merchant-alerts-modal"
                            onClick={async () => {
                                const errors: MerchantAutomationAuditList = item.hasOnboardingErrors
                                    ? (
                                          await executeListMerchantAudits({
                                              eventType: 'ERROR',
                                              nodeId: item.nodeId,
                                          })
                                      ).merchantAutomationAuditList
                                    : [];
                                const warnings: MerchantAutomationAuditList = item.hasOnboardingWarnings
                                    ? (
                                          await executeListMerchantAudits({
                                              eventType: 'WARNING',
                                              nodeId: item.nodeId,
                                          })
                                      ).merchantAutomationAuditList
                                    : [];
                                const alertMessages = [...errors, ...warnings].map((alert) => JSON.stringify(alert, undefined, 4));
                                setAlertMessages(alertMessages);
                                setVisible(true);
                            }}
                        >
                            <Icon name="status-warning" variant="error" />
                        </Button>
                    );
                }
                return (
                    <Button variant="link">
                        <Icon name="status-positive" variant="success" />
                    </Button>
                );
            },
            minWidth: 50,
        },
    ];

    return (
        <React.Fragment>
            <div className="main-header">
                <Header variant="h2">Stores Dashboard</Header>
            </div>
            <div className="main-content">
                <Container
                    header={
                        <Header
                            variant="h2"
                            description="Refine your search by selecting a search field and entering a search text below."
                        >
                            Search stores
                        </Header>
                    }
                >
                    <ColumnLayout columns={2}>
                        <div>
                            <FormField label="Alm physical store Id's">
                                <Input
                                    data-testid="search-text"
                                    value={nodeIdSearch}
                                    placeholder="Enter physical store Id's in comma-delimited form, e.g. nTAFGArJrc,Zg1EcM4rqK"
                                    onChange={({detail}) => {
                                        setNodeIdSearch(detail.value);
                                        setPaginationDisabled(!!detail.value);
                                    }}
                                />
                            </FormField>
                        </div>
                        <div>
                            <FormField label="Site Id">
                                <Input
                                    data-testid="search-siteId"
                                    value={siteId}
                                    placeholder="Enter warehouse Id, e.g. UBO4"
                                    onChange={({detail}) => {
                                        setSiteId(detail.value);
                                    }}
                                />
                            </FormField>
                        </div>
                        <div>
                            <FormField label="Onboarding Status">
                                <Multiselect
                                    data-testid="onboarding-status-select"
                                    selectedOptions={selectedOptions}
                                    onChange={({detail}) => setSelectedOptions(detail.selectedOptions)}
                                    deselectAriaLabel={(e) => `Remove ${e.label}`}
                                    options={[
                                        {
                                            label: 'Training Inputs Enabled for Delivery',
                                            value: 'training_inputs_delivery_enabled',
                                        },
                                        {
                                            label: 'Training Inputs Enabled for Pickup',
                                            value: 'training_inputs_pickup_enabled',
                                        },
                                        {
                                            label: 'Predictions Enabled',
                                            value: 'predictions_enabled',
                                        },
                                        {
                                            label: 'Unconstrained Demand Enabled',
                                            value: 'ud_enabled',
                                        },
                                    ]}
                                    placeholder="Choose onboarding status"
                                    selectedAriaLabel="Selected"
                                />
                            </FormField>
                        </div>
                        <div>
                            <FormField label="Filter by Onboarding Errors/Warnings">
                                <Multiselect
                                    data-testid="onboarding-alerts-select"
                                    selectedOptions={selectedAlertOptions}
                                    onChange={({detail}) => setSelectedAlertOptions(detail.selectedOptions)}
                                    deselectAriaLabel={(e) => `Remove ${e.label}`}
                                    options={[
                                        {
                                            label: 'Has Onboarding Errors',
                                            value: 'has_onboarding_errors',
                                        },
                                        {
                                            label: 'Has Onboarding Warnings',
                                            value: 'has_onboarding_warnings',
                                        },
                                    ]}
                                    placeholder="Find merchants with onboarding errors/warnings"
                                    selectedAriaLabel="Selected"
                                />
                            </FormField>
                        </div>
                        <div>
                            <Button
                                data-testid="search-button"
                                onClick={async () => {
                                    pageCount.current = 1;
                                    if (nodeIdSearch) {
                                        const response = await getMultipleMerchants.execute(nodeIdSearch.split(','));
                                        storeCount.current = response?.length ?? 0;
                                        setLocalMerchants(localMerchants.set(1, response ?? []));
                                        setPaginationDisabled(true);
                                        setNodeIdS(nodeIdSearch.split(','));
                                    } else {
                                        setPaginationDisabled(false);
                                        const onboardingStatus = selectedOptions.map((option: SelectProps.Option) => option.value!);
                                        const response = await excuteListMerchant({
                                            maxResults: pageSize,
                                            filterEquals: buildFilterMapAsJsonString(siteId, selectedAlertOptions),
                                            filterContains:
                                                onboardingStatus.length > 0 ? JSON.stringify({onboardingStatus}) : undefined,
                                        });
                                        storeCount.current = response?.merchants?.length ?? 0;
                                        setLocalMerchants(localMerchants.set(1, response?.merchants ?? []));
                                        setNextToken(response?.nextToken);
                                        setNodeIdS([]);
                                    }
                                    setCurrentPageIndex(1);
                                }}
                            >
                                Submit Search
                            </Button>
                        </div>
                        <div></div>
                    </ColumnLayout>
                </Container>
                <Table
                    data-testid="merchants"
                    selectionType={canManageMerchants ? 'multi' : undefined}
                    onSelectionChange={({detail}) => setSelectedItems(detail.selectedItems)}
                    selectedItems={selectedItems}
                    isItemDisabled={(item) =>
                        !!item.currentMerchantOnboardingStepFunctionExecutionId &&
                        !item.hasOnboardingWarnings &&
                        !item.hasOnboardingErrors
                    }
                    columnDefinitions={columnDefinitions}
                    items={localMerchants.get(currentPageIndex) ?? []}
                    loading={isLoading.current || getMultipleMerchantFetchStatus?.status === 'pending'}
                    loadingText="Loading merchants"
                    wrapLines
                    empty={
                        <Box textAlign="center" color="inherit">
                            <b>No merchants</b>
                        </Box>
                    }
                    header={
                        <Header
                            counter={`(${storeCount.current.toString()})`}
                            actions={
                                <SpaceBetween direction="horizontal" size="xs">
                                    {canManageMerchants ? (
                                        <React.Fragment>
                                            <Button
                                                data-testid="activate-store-button"
                                                disabled={selectedItems.length === 0}
                                                variant="primary"
                                                onClick={async () => {
                                                    const nodeIdString = selectedItems.map((item) => item.nodeId).join(',');
                                                    props.pushNotification({
                                                        content: `Stores ${nodeIdString} are being activated.`,
                                                        type: 'info',
                                                    });
                                                    await Promise.all(
                                                        selectedItems.map((item) => executeActivateMerchant({nodeId: item.nodeId}))
                                                    );
                                                    props.pushNotification({
                                                        content: `Activating stores ${nodeIdString} completed.`,
                                                        type: 'success',
                                                    });
                                                    setSelectedItems([]);
                                                }}
                                            >
                                                Activate Merchant
                                            </Button>
                                            <Button
                                                data-testid="inactivate-store-button"
                                                disabled={selectedItems.length === 0}
                                                variant="primary"
                                                onClick={async () => {
                                                    const nodeIdString = selectedItems.map((item) => item.nodeId).join(',');
                                                    props.pushNotification({
                                                        content: `Stores ${nodeIdString} are being inactivated/deprecated.`,
                                                        type: 'info',
                                                    });
                                                    await Promise.all(
                                                        selectedItems.map((item) =>
                                                            executeInactivateMerchant({nodeId: item.nodeId})
                                                        )
                                                    );
                                                    props.pushNotification({
                                                        content: `Inactivating/Deprecating stores ${nodeIdString} completed.`,
                                                        type: 'success',
                                                    });
                                                    setSelectedItems([]);
                                                }}
                                            >
                                                Inactivate Merchant
                                            </Button>
                                            <Button
                                                data-testid="onboarding-button"
                                                disabled={selectedItems.length === 0}
                                                variant="primary"
                                                onClick={async () => {
                                                    const nodeIdString = selectedItems.map((item) => item.nodeId).join(',');
                                                    props.pushNotification({
                                                        content: `Onnboarding process for stores ${nodeIdString} started/resumed.`,
                                                        type: 'info',
                                                    });
                                                    await Promise.all(
                                                        selectedItems.map((item) => executeOnboardMerchant({nodeId: item.nodeId}))
                                                    );
                                                    props.pushNotification({
                                                        content: `Start/resume onboarding for stores ${nodeIdString} completed.`,
                                                        type: 'success',
                                                    });
                                                    setSelectedItems([]);
                                                }}
                                            >
                                                Start/Resume Onboarding
                                            </Button>
                                        </React.Fragment>
                                    ) : (
                                        <React.Fragment></React.Fragment>
                                    )}
                                </SpaceBetween>
                            }
                        >
                            Stores found
                        </Header>
                    }
                    pagination={
                        <Pagination
                            data-testid="pagination"
                            ariaLabels={{
                                nextPageLabel: 'Next page',
                                previousPageLabel: 'Previous page',
                                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`,
                            }}
                            disabled={paginationDisabled}
                            currentPageIndex={currentPageIndex}
                            onChange={({detail}) => {
                                setCurrentPageIndex(detail.currentPageIndex);
                            }}
                            onNextPageClick={async ({detail}) => {
                                if (!detail.requestedPageAvailable && listMerchantsResponse?.nextToken) {
                                    pageCount.current = pageCount.current + 1;
                                    const onboardingStatus = selectedOptions.map((option: SelectProps.Option) => option.value);
                                    const response = await excuteListMerchant({
                                        maxResults: pageSize,
                                        nextToken: nextToken,
                                        filterEquals: buildFilterMapAsJsonString(siteId, selectedAlertOptions),
                                        filterContains:
                                            onboardingStatus.length > 0 ? JSON.stringify({onboardingStatus}) : undefined,
                                    });
                                    storeCount.current = storeCount.current + (response?.merchants?.length ?? 0);
                                    setLocalMerchants(localMerchants.set(detail.requestedPageIndex, response?.merchants ?? []));
                                    setNextToken(response?.nextToken);
                                }
                            }}
                            pagesCount={pageCount.current}
                            openEnd
                        />
                    }
                />
                <Modal
                    data-testid="merchant-alert-modal"
                    onDismiss={() => setVisible(false)}
                    visible={visible}
                    closeAriaLabel="Close modal"
                    size="medium"
                    header="Audit Events"
                >
                    {alertMessages.length
                        ? alertMessages.map((alert: string, i: number) => <pre key={i}>{alert}</pre>)
                        : 'No audit events.'}
                </Modal>
            </div>
        </React.Fragment>
    );
}
